import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Users, UserCheck, UserX, Shield, Search, Calendar, Send,
  ChevronLeft, ChevronRight, AlertTriangle,
} from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { coberturaAPI } from '../services/api';
import HeaderOrdenavel, { alternarOrdem } from '../components/HeaderOrdenavel';
import { baixarCsv, stampData } from '../utils/csv';
import SelecaoMultipla from '../components/SelecaoMultipla';

/**
 * Página /cobertura/segmento/:segmentoId
 *
 * Detalhe de um segmento: resumo de cobertura e lista dos contatos, com filtro
 * de presença em grupo. O caso de uso principal é isolar quem está no segmento
 * mas fora dos grupos e exportar esse recorte para remarketing.
 *
 * O filtro padrão é "fora de grupo", que é o público acionável.
 */
const CoberturaSegmento = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { segmentoId } = useParams();
  const navigate = useNavigate();
  const clienteId = user?.cliente_id;

  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [contatos, setContatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [exportando, setExportando] = useState(false);

  // 'fora' é o padrão: público de remarketing
  const [filtroPresenca, setFiltroPresenca] = useState('fora');
  const [filtroOptin, setFiltroOptin] = useState(true);
  const [busca, setBusca] = useState('');

  // Ordenação no servidor: a lista é paginada, ordenar só a página daria
  // resultado errado
  const [ordem, setOrdem] = useState({ campo: 'nome', dir: 'asc' });

  const ordenarPor = (campo) => {
    const dirInicial = campo === 'total_grupos' ? 'desc' : 'asc';
    setOrdem((atual) => alternarOrdem(atual, campo, dirInicial));
  };

  // Ferramentas de exportação só com ?export=true (padrão de Grupos do WhatsApp)
  const [searchParams] = useSearchParams();
  const showExport = searchParams.get('export') === 'true';

  // Recorte por período de entrada na base — critério principal de decisão
  const [periodo, setPeriodo] = useState('todos');
  const [filtroTags, setFiltroTags] = useState([]);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  const [filtroCidades, setFiltroCidades] = useState([]);
  const [cidadesDisponiveis, setCidadesDisponiveis] = useState([]);

  // Seleção para criar segmentação para campanha
  const [selecionados, setSelecionados] = useState(new Set());
  const [modalSegmentacao, setModalSegmentacao] = useState(false);
  const [nomeSegmentacao, setNomeSegmentacao] = useState('');
  const [criandoSegmentacao, setCriandoSegmentacao] = useState(false);
  const [usarTodosFiltrados, setUsarTodosFiltrados] = useState(false);

  /** Converte o período escolhido em data ISO de corte */
  const dataCorte = useCallback(() => {
    if (periodo === 'todos') return undefined;
    const agora = new Date();
    if (periodo === 'hoje') {
      const d = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
      return d.toISOString();
    }
    const dias = { '7d': 7, '30d': 30 }[periodo];
    if (!dias) return undefined;
    return new Date(agora.getTime() - dias * 24 * 60 * 60 * 1000).toISOString();
  }, [periodo]);

  const card = darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200';
  const txt = darkMode ? 'text-white' : 'text-gray-900';
  const txtSec = darkMode ? 'text-gray-400' : 'text-gray-500';

  const montarFiltros = useCallback(() => {
    const f = {};
    if (filtroOptin) f.optin = true;
    if (filtroPresenca === 'fora') f.em_grupo = false;
    if (filtroPresenca === 'dentro') f.em_grupo = true;
    const corte = dataCorte();
    if (corte) f.cadastrado_apos = corte;
    if (filtroTags.length) f.tags = filtroTags;
    if (filtroCidades.length) f.cidades = filtroCidades;
    return f;
  }, [filtroOptin, filtroPresenca, dataCorte, filtroTags, filtroCidades]);

  const loadResumo = useCallback(async () => {
    if (!clienteId || !segmentoId) return;
    try {
      // As opções de filtro vêm do próprio segmento: em "Cidade: São Paulo"
      // existe uma única cidade, oferecer as 395 da base não faz sentido
      const res = await coberturaAPI.getSegmento(clienteId, segmentoId, montarFiltros());
      if (res.data?.encontrado) {
        setResumo(res.data);
        setCidadesDisponiveis(res.data.cidades || []);
        setTagsDisponiveis((res.data.tags || []).map(t => ({ valor: t.tag, label: t.tag, total: t.total })));
      } else {
        setResumo(null);
      }
    } catch {
      setResumo(null);
    }
  }, [clienteId, segmentoId, montarFiltros]);

  const loadContatos = useCallback(async () => {
    if (!clienteId || !segmentoId) return;
    setLoading(true);
    try {
      const res = await coberturaAPI.getContatos(clienteId, {
        ...montarFiltros(),
        segmento_id: segmentoId,
        page,
        limit: 50,
        order_by: ordem.campo,
        order_dir: ordem.dir,
      });
      setContatos(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPages(res.data?.pages || 0);
    } catch (e) {
      toast.error('Erro ao carregar contatos do segmento');
    } finally {
      setLoading(false);
    }
  }, [clienteId, segmentoId, page, montarFiltros, ordem]);

  useEffect(() => { loadResumo(); }, [loadResumo]);
  useEffect(() => { loadContatos(); }, [loadContatos]);
  useEffect(() => { setPage(1); }, [filtroPresenca, filtroOptin, ordem, periodo, filtroTags, filtroCidades]);
  // Trocar de filtro invalida a seleção: os itens marcados podem não estar mais no recorte
  useEffect(() => {
    setSelecionados(new Set());
    setUsarTodosFiltrados(false);
  }, [filtroPresenca, filtroOptin, periodo, filtroTags, filtroCidades]);

  const alternarSelecao = (id) => {
    setSelecionados(prev => {
      const novo = new Set(prev);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });
    setUsarTodosFiltrados(false);
  };

  const alternarPagina = () => {
    const idsPagina = visiveis.map(c => c.id);
    const todosMarcados = idsPagina.every(id => selecionados.has(id));
    setSelecionados(prev => {
      const novo = new Set(prev);
      idsPagina.forEach(id => (todosMarcados ? novo.delete(id) : novo.add(id)));
      return novo;
    });
    setUsarTodosFiltrados(false);
  };

  /**
   * Cria a segmentação para campanha. Quando o usuário opta por "todos os filtrados",
   * manda apenas os filtros e o backend resolve o conjunto — evita trafegar
   * milhares de IDs e garante que o recorte seja o mesmo da tela.
   */
  const criarSegmentacao = async () => {
    const nome = nomeSegmentacao.trim();
    if (!nome) {
      toast.error('Informe um nome para a segmentação');
      return;
    }
    setCriandoSegmentacao(true);
    try {
      const filtros = usarTodosFiltrados
        ? { ...montarFiltros(), segmento_id: segmentoId, limite: total }
        : { contato_ids: [...selecionados] };

      const res = await coberturaAPI.criarSegmentacao(clienteId, nome, filtros);
      if (!res.data?.success) {
        toast.error(res.data?.message || 'Não foi possível criar a segmentação');
        return;
      }
      const r = res.data;
      toast.success(
        `Segmentação criada: ${r.vinculados} contatos (${r.contatos_criados} novos, ${r.contatos_existentes} já existiam)`,
      );
      if (r.ignorados_nao_contatar > 0) {
        toast.info(`${r.ignorados_nao_contatar} contatos foram excluídos por pedido de não contato`);
      }
      if (r.sem_optin > 0) {
        toast.warning(`Atenção: ${r.sem_optin} contatos da segmentação não têm opt-in`);
      }
      setModalSegmentacao(false);
      setNomeSegmentacao('');
      setSelecionados(new Set());
      // Vai para a criação da campanha com a segmentação já selecionada.
      // A rota é /nova (a lista não abre o formulário) e o form lê grupo_id
      // da query string.
      navigate(`/marketing/campanhas/nova?grupo_id=${r.grupo_id}&origem=cobertura`);
    } catch (e) {
      toast.error('Erro ao criar segmentação');
    } finally {
      setCriandoSegmentacao(false);
    }
  };

  const qtdSelecionada = usarTodosFiltrados ? total : selecionados.size;

  /**
   * Exporta TODOS os contatos do segmento com os filtros atuais, sem paginação.
   * Passa pelo backend porque contém dado pessoal e a exportação precisa ser
   * registrada para auditoria LGPD.
   */
  const exportar = async () => {
    setExportando(true);
    try {
      const res = await coberturaAPI.exportar(clienteId, {
        ...montarFiltros(),
        segmento_id: segmentoId,
        order_by: ordem.campo,
        order_dir: ordem.dir,
      });
      baixarCsv(`cobertura-${resumo?.segmento_alias || 'segmento'}-${stampData()}`, res.data);
      toast.success('CSV exportado');
    } catch (e) {
      toast.error('Erro ao exportar CSV');
    } finally {
      setExportando(false);
    }
  };

  const visiveis = busca.trim()
    ? contatos.filter(c =>
        [c.nome, c.telefone, c.email, c.cidade, (c.grupos || []).join(' ')]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(busca.trim().toLowerCase())))
    : contatos;

  const fmt = (n) => (n === undefined || n === null ? '—' : Number(n).toLocaleString('pt-BR'));

  const cards = [
    { label: 'Total no segmento', valor: resumo?.total, icon: Users, cor: 'text-blue-500' },
    { label: 'Já em grupo', valor: resumo?.ja_em_grupo, icon: UserCheck, cor: 'text-green-500' },
    { label: 'Fora de grupo', valor: resumo?.fora_grupo, icon: UserX, cor: 'text-red-500' },
    { label: 'Fora e com optin', valor: resumo?.com_optin, icon: Shield, cor: 'text-purple-500' },
  ];

  return (
    <Layout>
      <PageHeader
        title={resumo?.segmento_nome || 'Segmento'}
        subtitle={resumo ? `Taxa de cobertura: ${resumo.taxa_cobertura}%` : 'Carregando...'}
        actions={
          <button
            onClick={() => navigate('/cobertura')}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${card} ${txt}`}
          >
            <ArrowLeft size={16} /> Voltar
          </button>
        }
      />

      {/* Ferramentas de exportação — só com ?export=true, para não poluir o layout
          (mesmo padrão de Grupos do WhatsApp). A exportação sempre traz o
          conjunto completo da query, nunca apenas a página visível. */}
      {showExport && (
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          <button
            onClick={exportar}
            disabled={exportando || total === 0}
            title="Exporta todos os contatos do segmento com os filtros atuais, sem paginação"
            className={`text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 ${
              darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            📥 {exportando ? 'Exportando...' : `Exportar ${total.toLocaleString('pt-BR')} contatos (CSV)`}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {cards.map(({ label, valor, icon: Icon, cor }) => (
          <div key={label} className={`border rounded-xl p-4 ${card}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${txtSec}`}>{label}</span>
              <Icon size={16} className={cor} />
            </div>
            <p className={`text-2xl font-semibold ${txt}`}>{fmt(valor)}</p>
          </div>
        ))}
      </div>

      {filtroOptin && resumo && resumo.fora_grupo > resumo.com_optin && (
        <div className={`border rounded-xl p-3 mb-4 flex items-start gap-2 ${
          darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
        }`}>
          <AlertTriangle size={16} className="text-yellow-500 mt-0.5 shrink-0" />
          <p className={`text-xs ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
            {fmt(resumo.fora_grupo - resumo.com_optin)} contatos deste segmento estão fora de grupo
            mas <strong>sem optin</strong>. Estão ocultos porque disparar para eles é exposição LGPD.
            Desligue o filtro de optin para vê-los.
          </p>
        </div>
      )}

      <div className={`border rounded-xl p-3 mb-4 flex flex-wrap items-center gap-3 ${card}`}>
        <div className="flex items-center gap-1">
          {[
            { v: 'fora', l: 'Fora de grupo' },
            { v: 'dentro', l: 'Em grupo' },
            { v: 'todos', l: 'Todos' },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setFiltroPresenca(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filtroPresenca === v
                  ? 'bg-orange-600 text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Recorte por entrada na base: "quem entrou hoje" */}
        <div className="flex items-center gap-1 border-l pl-3 border-gray-300 dark:border-zinc-600">
          <Calendar size={14} className={txtSec} />
          {[
            { v: 'hoje', l: 'Hoje' },
            { v: '7d', l: '7 dias' },
            { v: '30d', l: '30 dias' },
            { v: 'todos', l: 'Sempre' },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setPeriodo(v)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                periodo === v
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <span className={`text-xs ${txtSec}`}>Apenas optados</span>
          <input
            type="checkbox"
            checked={filtroOptin}
            onChange={(e) => setFiltroOptin(e.target.checked)}
            className="rounded accent-orange-600"
          />
        </label>

        {cidadesDisponiveis.length > 0 && (
          <SelecaoMultipla
            opcoes={cidadesDisponiveis}
            valores={filtroCidades}
            onChange={setFiltroCidades}
            placeholder="Todas as cidades"
            titulo="Filtrar por uma ou mais cidades"
            darkMode={darkMode}
          />
        )}

        {tagsDisponiveis.length > 0 && (
          <SelecaoMultipla
            opcoes={tagsDisponiveis}
            valores={filtroTags}
            onChange={setFiltroTags}
            placeholder="Todas as tags"
            titulo="Filtrar por uma ou mais tags de origem"
            darkMode={darkMode}
          />
        )}

        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${txtSec}`} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Filtrar nesta página por nome, telefone, e-mail, cidade ou grupo"
            className={`w-full pl-9 pr-3 py-1.5 rounded-lg text-xs border ${
              darkMode ? 'bg-zinc-900 border-zinc-700 text-white' : 'bg-white border-gray-300'
            }`}
          />
        </div>

        <span className={`text-xs ${txtSec}`}>{fmt(total)} contatos</span>
      </div>

      {/* Barra de ação: aparece quando há seleção */}
      {qtdSelecionada > 0 && (
        <div className={`border rounded-xl p-3 mb-4 flex flex-wrap items-center justify-between gap-3 ${
          darkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-sm font-medium ${darkMode ? 'text-orange-200' : 'text-orange-900'}`}>
              {fmt(qtdSelecionada)} {qtdSelecionada === 1 ? 'contato selecionado' : 'contatos selecionados'}
            </span>
            {!usarTodosFiltrados && total > visiveis.length && (
              <button
                onClick={() => setUsarTodosFiltrados(true)}
                className={`text-xs underline ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}
              >
                Selecionar todos os {fmt(total)} do filtro atual
              </button>
            )}
            <button
              onClick={() => { setSelecionados(new Set()); setUsarTodosFiltrados(false); }}
              className={`text-xs ${txtSec} underline`}
            >
              Limpar
            </button>
          </div>
          <button
            onClick={() => {
              setNomeSegmentacao(`${resumo?.segmento_nome || 'Segmento'}${periodo !== 'todos' ? ' - ' + periodo : ''}`);
              setModalSegmentacao(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:bg-orange-700"
          >
            <Send size={15} /> Criar segmentação para campanha
          </button>
        </div>
      )}

      <div className={`border rounded-xl overflow-hidden ${card}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={darkMode ? 'bg-zinc-900' : 'bg-gray-50'}>
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={visiveis.length > 0 && visiveis.every(c => selecionados.has(c.id))}
                    onChange={alternarPagina}
                    title="Selecionar/desmarcar os contatos desta página"
                    className="rounded accent-orange-600 cursor-pointer"
                  />
                </th>
                <HeaderOrdenavel campo="nome" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}>Nome</HeaderOrdenavel>
                <HeaderOrdenavel campo="telefone" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}>Telefone</HeaderOrdenavel>
                <HeaderOrdenavel campo="cidade" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}>Cidade</HeaderOrdenavel>
                <HeaderOrdenavel campo="data_cadastro" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}
                  title="Ordenar por quando o contato entrou na base">
                  Entrou em
                </HeaderOrdenavel>
                <HeaderOrdenavel campo="optin" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}>Optin</HeaderOrdenavel>
                <HeaderOrdenavel campo="total_grupos" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}
                  title="Ordenar por quantidade de grupos — quem está em mais grupos tem maior alcance">
                  Grupos
                </HeaderOrdenavel>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-zinc-700' : 'divide-gray-200'}`}>
              {loading ? (
                <tr><td colSpan={7} className={`px-4 py-8 text-center text-sm ${txtSec}`}>Carregando...</td></tr>
              ) : visiveis.length === 0 ? (
                <tr><td colSpan={7} className={`px-4 py-8 text-center text-sm ${txtSec}`}>
                  Nenhum contato com os filtros atuais.
                </td></tr>
              ) : visiveis.map((c) => {
                // grupos vem como array do backend: nomes contem virgula
                const listaGrupos = Array.isArray(c.grupos) ? c.grupos : [];
                const marcado = usarTodosFiltrados || selecionados.has(c.id);
                return (
                  <tr key={c.id} className={marcado ? (darkMode ? 'bg-orange-900/10' : 'bg-orange-50/50') : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={marcado}
                        disabled={usarTodosFiltrados}
                        onChange={() => alternarSelecao(c.id)}
                        className="rounded accent-orange-600 cursor-pointer disabled:opacity-60"
                      />
                    </td>
                    <td className={`px-4 py-2 text-sm ${txt}`}>
                      {c.nome || '—'}
                      {c.email && <span className={`block text-xs ${txtSec}`}>{c.email}</span>}
                      {c.tags?.length > 0 && (
                        <span className="flex flex-wrap gap-1 mt-0.5">
                          {c.tags.slice(0, 3).map((t, i) => (
                            <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] ${
                              darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                            }`}>{t}</span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-2 text-sm font-mono text-xs ${txt}`}>{c.telefone || '—'}</td>
                    <td className={`px-4 py-2 text-sm ${txtSec}`}>{c.cidade || '—'}</td>
                    <td className={`px-4 py-2 text-xs ${txtSec}`}>
                      {c.data_cadastro
                        ? new Date(c.data_cadastro).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        c.optin
                          ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                          : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                      }`}>{c.optin ? 'sim' : 'não'}</span>
                    </td>
                    <td className="px-4 py-2 text-sm max-w-md">
                      {listaGrupos.length === 0 ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
                        }`}>fora de grupos</span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1" title={listaGrupos.join('\n')}>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                          }`}>{c.total_grupos ?? listaGrupos.length}</span>
                          {listaGrupos.slice(0, 2).map((g, i) => (
                            <span key={i} className={`px-2 py-0.5 rounded text-xs truncate max-w-[150px] ${
                              darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}>{g}</span>
                          ))}
                          {listaGrupos.length > 2 && (
                            <span className={`text-xs ${txtSec}`}>+{listaGrupos.length - 2}</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${
            darkMode ? 'border-zinc-700' : 'border-gray-200'
          }`}>
            <span className={`text-xs ${txtSec}`}>Página {page} de {pages}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className={`p-1.5 rounded-lg border disabled:opacity-40 ${card} ${txt}`}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className={`p-1.5 rounded-lg border disabled:opacity-40 ${card} ${txt}`}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Modal: criar segmentação para campanha */}
      {modalSegmentacao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl border p-5 ${card}`}>
            <h3 className={`text-lg font-semibold mb-1 ${txt}`}>Criar segmentação para campanha</h3>
            <p className={`text-xs mb-4 ${txtSec}`}>
              Os contatos serão importados e agrupados numa segmentação pronta para campanha.
            </p>

            <label className={`block text-xs font-medium mb-1 ${txtSec}`}>Nome da segmentação</label>
            <input
              value={nomeSegmentacao}
              onChange={(e) => setNomeSegmentacao(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && criarSegmentacao()}
              autoFocus
              placeholder="Ex.: Tarifa Zero - 26/08"
              className={`w-full px-3 py-2 rounded-lg text-sm border mb-4 ${
                darkMode ? 'bg-zinc-900 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            />

            <div className={`rounded-lg p-3 mb-4 text-xs space-y-1 ${
              darkMode ? 'bg-zinc-900' : 'bg-gray-50'
            }`}>
              <div className={`flex justify-between ${txt}`}>
                <span>Contatos</span>
                <strong>{fmt(qtdSelecionada)}</strong>
              </div>
              <div className={`flex justify-between ${txtSec}`}>
                <span>Origem</span>
                <span>{resumo?.segmento_nome || 'segmento'}</span>
              </div>
              {periodo !== 'todos' && (
                <div className={`flex justify-between ${txtSec}`}>
                  <span>Período</span>
                  <span>{{ hoje: 'entraram hoje', '7d': 'últimos 7 dias', '30d': 'últimos 30 dias' }[periodo]}</span>
                </div>
              )}
              <div className={`flex justify-between ${txtSec}`}>
                <span>Presença em grupo</span>
                <span>{{ fora: 'fora de grupos', dentro: 'já em grupos', todos: 'todos' }[filtroPresenca]}</span>
              </div>
              <div className={`flex justify-between ${txtSec}`}>
                <span>Opt-in</span>
                <span>{filtroOptin ? 'apenas optados' : 'sem filtro de opt-in'}</span>
              </div>
            </div>

            {!filtroOptin && (
              <div className={`rounded-lg p-2.5 mb-4 flex items-start gap-2 ${
                darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <AlertTriangle size={14} className="text-yellow-500 mt-0.5 shrink-0" />
                <p className={`text-xs ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  A segmentação pode incluir contatos sem opt-in. Disparar para eles é exposição LGPD.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setModalSegmentacao(false)}
                className={`px-4 py-2 rounded-lg text-sm border ${card} ${txt}`}
              >
                Cancelar
              </button>
              <button
                onClick={criarSegmentacao}
                disabled={criandoSegmentacao || !nomeSegmentacao.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {criandoSegmentacao ? 'Criando...' : 'Criar e ir para campanha'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CoberturaSegmento;
